import React, { useEffect, useRef, useState } from 'react';
import { X, Play, Pause, RotateCcw, Undo2, AlertCircle } from 'lucide-react';

interface Props {
    /** Object URL of everything recorded so far (the recorder is paused while this panel is open). */
    url: string;
    /** The recording's own clock at the moment it was paused: where a re-take will begin. */
    endMs: number;
    vn: boolean;
    /** Stretches already discarded in this session, on the recording's clock. */
    cuts: { fromMs: number; toMs: number }[];
    /** Discard everything from this point of the recording up to the pause. */
    onPick: (targetMs: number) => void;
    /** Take back the last discard (only possible until recording resumes). */
    onUndo?: () => void;
    onClose: () => void;
    onResume: () => void;
}

const clock = (secs: number) => {
    const s = Math.max(0, secs);
    const m = Math.floor(s / 60);
    return `${m}:${(s - m * 60).toFixed(1).padStart(4, '0')}`;
};

/**
 * Re-watch what was just recorded and choose the exact moment to re-record from.
 *
 * A recording still in progress is a WebM without a duration or a seek index, so the browser reports
 * `duration = Infinity` until it has been made to scan to the end once; that is done on load. The
 * position in this video is the position on the recording's own clock (paused time is in neither).
 */
export default function RecordingRewatchPanel({ url, endMs, vn, cuts, onPick, onUndo, onClose, onResume }: Props) {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [ready, setReady] = useState(false);
    const [duration, setDuration] = useState(0);
    const [now, setNow] = useState(0);
    const [playing, setPlaying] = useState(false);
    const [picked, setPicked] = useState<number | null>(null); // seconds
    const stopAt = useRef<number | null>(null);
    const endSecs = endMs / 1000;

    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;
        let scanning = false;
        const settle = (d: number) => {
            setDuration(d);
            setReady(true);
            // The mistake is recent: open a little before the end rather than at 0:00.
            v.currentTime = Math.max(0, d - 12);
        };
        const onMeta = () => {
            if (Number.isFinite(v.duration) && v.duration > 0) { settle(v.duration); return; }
            scanning = true;
            v.currentTime = 1e7; // forces the scan that discovers the real length
        };
        const onDuration = () => {
            if (scanning && Number.isFinite(v.duration) && v.duration > 0) { scanning = false; settle(v.duration); }
        };
        const onSeeked = () => {
            if (scanning && Number.isFinite(v.currentTime) && v.currentTime > 0) { scanning = false; settle(v.currentTime); }
        };
        const onTime = () => {
            setNow(v.currentTime);
            if (stopAt.current != null && v.currentTime >= stopAt.current) { v.pause(); stopAt.current = null; }
        };
        const onPlay = () => setPlaying(true);
        const onPause = () => setPlaying(false);
        v.addEventListener('loadedmetadata', onMeta);
        v.addEventListener('durationchange', onDuration);
        v.addEventListener('seeked', onSeeked);
        v.addEventListener('timeupdate', onTime);
        v.addEventListener('play', onPlay);
        v.addEventListener('pause', onPause);
        return () => {
            v.removeEventListener('loadedmetadata', onMeta);
            v.removeEventListener('durationchange', onDuration);
            v.removeEventListener('seeked', onSeeked);
            v.removeEventListener('timeupdate', onTime);
            v.removeEventListener('play', onPlay);
            v.removeEventListener('pause', onPause);
        };
    }, [url]);

    const total = duration || endSecs;
    // If the file's length and the recording clock disagree, a point chosen here would land
    // somewhere else in the export. Say so instead of cutting in the wrong place.
    const drift = ready ? Math.abs(duration - endSecs) : 0;
    const unreliable = drift > 1.5;

    const seek = (t: number) => {
        const v = videoRef.current;
        if (!v) return;
        stopAt.current = null;
        v.currentTime = Math.max(0, Math.min(t, total));
    };
    const nudge = (d: number) => seek((videoRef.current?.currentTime ?? 0) + d);
    const toggle = () => {
        const v = videoRef.current;
        if (!v) return;
        stopAt.current = picked;
        if (v.paused) void v.play(); else v.pause();
    };
    const pick = () => {
        const v = videoRef.current;
        if (!v) return;
        v.pause();
        const t = v.currentTime;
        setPicked(t);
        onPick(Math.round(t * 1000));
    };
    const replayLeadIn = () => {
        const v = videoRef.current;
        if (!v || picked == null) return;
        v.currentTime = Math.max(0, picked - 6);
        stopAt.current = picked;
        void v.play();
    };
    const undo = () => { setPicked(null); stopAt.current = null; onUndo?.(); };

    const pct = (t: number) => `${total > 0 ? Math.max(0, Math.min(100, (t / total) * 100)) : 0}%`;
    const dropSecs = picked != null ? Math.max(0, endSecs - picked) : Math.max(0, endSecs - now);

    return (
        <div className="fixed inset-0 z-[900] bg-black/70 flex items-center justify-center p-4">
            <div className="bg-gray-900 text-gray-100 rounded-2xl shadow-2xl border border-white/10 w-full max-w-3xl max-h-[94vh] flex flex-col">
                <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
                    <div className="min-w-0">
                        <h3 className="font-bold text-sm">{vn ? 'Xem lại và quay lại từ một điểm' : 'Re-watch and re-record from a point'}</h3>
                        <p className="text-[11px] text-gray-400">{vn ? 'Đang tạm dừng — phần xem lại này không được ghi vào video.' : 'Recording is paused — this review is not recorded.'}</p>
                    </div>
                    <button onClick={onClose} className="p-1.5 hover:bg-white/10 rounded-lg" title={vn ? 'Đóng (vẫn tạm dừng)' : 'Close (stays paused)'}>
                        <X size={15} />
                    </button>
                </div>

                <div className="p-4 overflow-y-auto space-y-3">
                    <video ref={videoRef} src={url} playsInline preload="auto" className="w-full max-h-[46vh] rounded-xl bg-black" onClick={toggle} />

                    {!ready && (
                        <p className="text-xs text-gray-400">{vn ? 'Đang nạp phần đã quay…' : 'Loading what you recorded…'}</p>
                    )}

                    {ready && (
                        <>
                            {/* Timeline: discarded stretches in red, the chosen point as a marker */}
                            <div className="relative h-7 select-none">
                                <div className="absolute inset-x-0 top-3 h-1.5 rounded bg-white/15" />
                                {cuts.map((c, i) => (
                                    <div key={i} className="absolute top-3 h-1.5 bg-red-500/70 rounded" style={{ left: pct(c.fromMs / 1000), width: `calc(${pct(c.toMs / 1000)} - ${pct(c.fromMs / 1000)})` }} />
                                ))}
                                {picked == null && (
                                    <div className="absolute top-3 h-1.5 bg-red-500/30 rounded" style={{ left: pct(now), right: 0 }} />
                                )}
                                <input
                                    type="range" min={0} max={total} step={0.05} value={Math.min(now, total)}
                                    onChange={e => seek(Number(e.target.value))}
                                    className="absolute inset-x-0 top-0 w-full h-7 opacity-0 cursor-pointer"
                                    aria-label={vn ? 'Vị trí' : 'Position'}
                                />
                                <div className="absolute top-1 w-0.5 h-5 bg-white pointer-events-none" style={{ left: pct(now) }} />
                            </div>

                            <div className="flex items-center justify-between gap-2 flex-wrap">
                                <div className="flex items-center gap-1.5">
                                    <button onClick={() => nudge(-5)} className="px-2 py-1.5 text-xs font-semibold rounded-lg bg-white/10 hover:bg-white/20">−5s</button>
                                    <button onClick={() => nudge(-0.5)} className="px-2 py-1.5 text-xs font-semibold rounded-lg bg-white/10 hover:bg-white/20">−0.5s</button>
                                    <button onClick={toggle} className="p-2 rounded-lg bg-white/15 hover:bg-white/25" title={playing ? (vn ? 'Dừng' : 'Pause') : (vn ? 'Phát' : 'Play')}>
                                        {playing ? <Pause size={14} /> : <Play size={14} />}
                                    </button>
                                    <button onClick={() => nudge(0.5)} className="px-2 py-1.5 text-xs font-semibold rounded-lg bg-white/10 hover:bg-white/20">+0.5s</button>
                                    <button onClick={() => nudge(5)} className="px-2 py-1.5 text-xs font-semibold rounded-lg bg-white/10 hover:bg-white/20">+5s</button>
                                </div>
                                <span className="text-xs tabular-nums text-gray-300">{clock(now)} / {clock(total)}</span>
                            </div>

                            {unreliable && (
                                <div className="flex items-start gap-2 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-xl px-3 py-2">
                                    <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                                    <span>
                                        {vn
                                            ? `Độ dài file (${clock(duration)}) lệch với đồng hồ quay (${clock(endSecs)}) ${drift.toFixed(1)} giây. Điểm chọn ở đây có thể lệch trong video xuất ra — hãy chọn sớm hơn một chút cho an toàn.`
                                            : `The file length (${clock(duration)}) and the recording clock (${clock(endSecs)}) differ by ${drift.toFixed(1)}s. A point chosen here may be off in the export — choose a little earlier to be safe.`}
                                    </span>
                                </div>
                            )}

                            {picked == null ? (
                                <div className="flex items-center justify-between gap-3 flex-wrap bg-white/5 rounded-xl px-3 py-2.5">
                                    <p className="text-xs text-gray-300">
                                        {vn
                                            ? <>Dừng ở đúng chỗ bạn muốn nói lại. Từ <b>{clock(now)}</b> đến cuối (<b>{dropSecs.toFixed(1)} giây</b>) sẽ bị bỏ khỏi video.</>
                                            : <>Stop exactly where you want to speak again. From <b>{clock(now)}</b> to the end (<b>{dropSecs.toFixed(1)}s</b>) will be left out of the video.</>}
                                    </p>
                                    <button
                                        onClick={pick}
                                        disabled={dropSecs < 0.3}
                                        className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded-xl bg-red-500 hover:bg-red-600 disabled:opacity-40 text-white"
                                    >
                                        <RotateCcw size={12} /> {vn ? 'Quay lại từ đây' : 'Re-record from here'}
                                    </button>
                                </div>
                            ) : (
                                <div className="space-y-2 bg-emerald-500/10 border border-emerald-500/30 rounded-xl px-3 py-2.5">
                                    <p className="text-xs text-emerald-200">
                                        {vn
                                            ? <>Đã đặt điểm quay lại tại <b>{clock(picked)}</b>. <b>{dropSecs.toFixed(1)} giây</b> sau đó sẽ không có trong video. Bấm tiếp tục quay rồi nói lại từ chỗ này.</>
                                            : <>Re-record point set at <b>{clock(picked)}</b>. The <b>{dropSecs.toFixed(1)}s</b> after it will not be in the video. Resume and speak again from there.</>}
                                    </p>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <button onClick={replayLeadIn} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-white/10 hover:bg-white/20">
                                            <Play size={11} /> {vn ? 'Nghe lại 6 giây trước điểm này' : 'Replay the 6s before this point'}
                                        </button>
                                        {onUndo && (
                                            <button onClick={undo} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-white/10 hover:bg-white/20">
                                                <Undo2 size={11} /> {vn ? 'Hoàn tác, chọn lại' : 'Undo, choose again'}
                                            </button>
                                        )}
                                        <button onClick={onResume} className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-bold rounded-lg bg-green-500 hover:bg-green-600 text-white ml-auto">
                                            <Play size={11} fill="white" /> {vn ? 'Tiếp tục quay' : 'Resume recording'}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
